import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthRepositoryService } from '../../repositories/ecommerce/auth.repository.service';
import {
  EcommerceAccountModel,
  EcommerceLoginRequestModel,
  EcommerceRegisterRequestModel,
  EcommerceRegisterResponseModel,
  MessageResponseModel,
  PasswordChangeRequestModel,
} from '../../models/ecommerce/auth.model';
import { EcommerceSessionService } from './ecommerce-session.service';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class EcommerceAuthService {
  private readonly repo = inject(AuthRepositoryService);
  private readonly sessionService = inject(EcommerceSessionService);
  private readonly keycloakAuth = inject(AuthService);
  private readonly accountSignal = signal<EcommerceAccountModel | null>(null);

  readonly account = computed(() => this.accountSignal());

  /**
   * Autenticato verso l'ecommerce: con Keycloak (via principale dal
   * 15/09/2026, il Bearer JWT è accettato da tutte le API ecommerce e
   * auto-collega l'account al primo uso) oppure con la sessione legacy di
   * `POST /auth/login`, mantenuta come ripiego per `mode: legacy`.
   */
  readonly isAuthenticated = computed(
    () => this.keycloakAuth.isAuthenticatedSignal() || this.sessionService.isAuthenticated()
  );

  /** Avvia il login Keycloak; al ritorno si atterra su `returnUrl`. */
  async loginWithKeycloak(returnUrl?: string): Promise<void> {
    await this.keycloakAuth.login(returnUrl);
  }

  /**
   * Registrazione standalone. Non apre una sessione: il contratto richiede la
   * conferma dell'email, quindi il chiamante deve invitare l'utente a
   * controllare la casella e poi accedere.
   */
  async register(
    payload: EcommerceRegisterRequestModel
  ): Promise<EcommerceRegisterResponseModel> {
    const response = await firstValueFrom(this.repo.register(payload));
    this.accountSignal.set(response.account);
    return response;
  }

  async login(payload: EcommerceLoginRequestModel): Promise<EcommerceAccountModel> {
    const response = await firstValueFrom(this.repo.login(payload));
    this.sessionService.setSession(response.session);
    this.accountSignal.set(response.account);

    try {
      return (await this.loadCurrentAccount()) ?? response.account;
    } catch {
      return response.account;
    }
  }

  async loadCurrentAccount(): Promise<EcommerceAccountModel | null> {
    if (!this.isAuthenticated()) {
      this.accountSignal.set(null);
      return null;
    }

    const account = await firstValueFrom(this.repo.me());
    this.accountSignal.set(account);
    return account;
  }

  /**
   * Chiude la sessione legacy se presente e poi, se l'utente è su Keycloak,
   * esegue il logout Keycloak (che lascia la pagina e torna su `returnUrl`).
   */
  async logout(returnUrl?: string): Promise<void> {
    try {
      if (this.sessionService.isAuthenticated()) {
        await firstValueFrom(this.repo.logout());
      }
    } catch {
      // La sessione locale va comunque eliminata: il server la farà scadere.
    } finally {
      this.accountSignal.set(null);
      this.sessionService.clearSession();
    }

    if (this.keycloakAuth.isAuthenticatedSignal()) {
      await this.keycloakAuth.logout(returnUrl);
    }
  }

  /** Rinvia l'email di conferma per un account non ancora verificato. */
  async resendConfirmation(email: string): Promise<MessageResponseModel> {
    return firstValueFrom(this.repo.resendConfirmation({ email }));
  }

  /** Conferma l'indirizzo email; l'accesso resta da fare con `login()`. */
  async confirmEmail(token: string): Promise<MessageResponseModel> {
    return firstValueFrom(this.repo.confirmEmail({ token }));
  }

  // ---------------------------------------------------------------------------
  // Password (guida v3, §2.4). Il backend dialoga con Keycloak, quindi la
  // password impostata qui vale anche per il login Keycloak.
  // ---------------------------------------------------------------------------

  async requestPasswordReset(email: string): Promise<MessageResponseModel> {
    return firstValueFrom(this.repo.requestPasswordReset({ email }));
  }

  async confirmPasswordReset(token: string, password: string): Promise<MessageResponseModel> {
    return firstValueFrom(this.repo.confirmPasswordReset({ token, password }));
  }

  async changePassword(payload: PasswordChangeRequestModel): Promise<MessageResponseModel> {
    return firstValueFrom(this.repo.changePassword(payload));
  }
}
